import { Autocomplete, createFilterOptions, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React, { useMemo, useState, useCallback } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';
import ConfirmationDialog from '../../../../common/confirmationDialog';
import TableItemWrapper from './components/TableItemWrapper';
import TableFilter from './components/TableFilter';

const OPTIONS_LIMIT = 100;
const defaultFilterOptions = createFilterOptions();

const filterOptions = (options, state) => {
  return defaultFilterOptions(options, state).slice(0, OPTIONS_LIMIT);
};

const ConfigurationApiTable = ({
  sources,
  itemsHashBySource,
  apiConfigurationHash,
  autocompleteLabel,
  onSave,
  onChange,
  onAddRow,
  onDeleteRow,
  onClose,
  onAiMatch,
  maxHeight,
  aiMatchLoading,
  showFilter
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [filter, setFilter] = useState('');

  const disabledIdsHash = useMemo(() => {
    const data = {};
    for (let key in apiConfigurationHash) {
      const currentData = apiConfigurationHash[key];
      for (let sourceKey in currentData) {
        const sourceData = currentData[sourceKey];
        if (sourceData.id !== null) {
          data[sourceData.id] = sourceData.id;
        }
      }
    }
    return data;
  }, [apiConfigurationHash]);

  const filteredHash = useMemo(() => {
    if (!showFilter || !filter) {
      return apiConfigurationHash;
    }
    const data = {};
    for (let key in apiConfigurationHash) {
      let name = '';
      Object.values(apiConfigurationHash[key]).forEach((item) => {
        name += `${itemsHashBySource[item.source][item.id].name}/`;
      });
      if (name.toLowerCase().includes(filter.toLowerCase())) {
        data[key] = apiConfigurationHash[key];
      }
    }
    return data;
  }, [apiConfigurationHash, filter, showFilter, itemsHashBySource]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setDialogData(null);
  }, []);

  const handleDeleteRow = useCallback((unionKey) => {
    setIsDialogOpen(true);
    setDialogData({
      title: 'Delete row',
      content: 'Are you sure you want to delete this row?',
      onConfirm: () => {
        onDeleteRow(unionKey);
        handleCloseDialog();
      },
      onClose: () => handleCloseDialog(),
    })
  }, [onDeleteRow, handleCloseDialog]);

  const handleSave = useCallback(() => {
    setIsDialogOpen(true);
    setDialogData({
      title: 'Confirm merge',
      content: 'Are you sure you want to save?',
      onConfirm: () => {
        onSave();
        handleCloseDialog();
      },
      onClose: () => handleCloseDialog(),
    })
  }, [handleCloseDialog, onSave]);

  return (
    <Box sx={{ height: '100%' }}>
      {dialogData && <ConfirmationDialog open={isDialogOpen} title={dialogData.title} onClose={dialogData.onClose} onConfirm={dialogData.onConfirm} content={dialogData.content} />}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px', justifyContent: 'space-between', height: '100%' }}>
        <TableContainer style={{ maxHeight: maxHeight || 'calc(100vh - 350px)' }}>
          <Table stickyHeader sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 50 }}></TableCell>
                {sources && !!sources.length && sources.map((item) => <TableCell key={item.id} style={{ minWidth: '150px', textAlign: 'center' }}>
                  <Typography variant='h5'>
                    {item.name}
                  </Typography>
                </TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(filteredHash).map((unionKey) => (
                <TableItemWrapper
                  key={unionKey}
                  height={73}
                >
                  <TableCell style={{ width: 'fit-content', textAlign: 'center', verticalAlign: 'top' }}>
                    <DeleteIcon color='error' fontSize='large' style={{ cursor: 'pointer' }} onClick={() => handleDeleteRow(unionKey)} />
                  </TableCell>
                  {sources && !!sources.length && sources.map((source, index) => <TableCell key={`${source.id}-${index}`} component="th" scope="row" style={{ minWidth: '150px', verticalAlign: 'top' }}>
                    <Autocomplete
                      disablePortal
                      filterOptions={filterOptions}
                      options={Object.values(itemsHashBySource[source.id])}
                      getOptionDisabled={(option) => !!disabledIdsHash[option.id]}
                      getOptionLabel={(option) => `${option.name} (${option.id})`}
                      value={itemsHashBySource[source.id][apiConfigurationHash[unionKey][source.id]?.id] || null}
                      size='small'
                      onChange={(e, item) => onChange(unionKey, source.id, item)}
                      renderInput={(params) => <TextField {...params} label={autocompleteLabel} helperText={apiConfigurationHash[unionKey][source.id]?.accuracy ? `Accuracy rate: ${apiConfigurationHash[unionKey][source.id]?.accuracy}%` : null} />}
                    />
                  </TableCell>)}
                </TableItemWrapper>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            {onAddRow && <LoadingButton
              variant="contained"
              color={"info"}
              sx={{ padding: '6px', minWidth: 'fit-content' }}
              loading={!Object.values(itemsHashBySource).length}
              onClick={onAddRow}
            >
              {<AddCircleOutlineIcon />}
            </LoadingButton>}
            {onAiMatch && <LoadingButton
              variant="contained"
              color={"info"}
              onClick={onAiMatch}
              loading={aiMatchLoading}
            >
              AI Match
            </LoadingButton>}
            {onSave && <Button
              variant="contained"
              color={"success"}
              onClick={handleSave}
            >
              Save
            </Button>}
            {showFilter && <TableFilter delay={300} onChange={(value) => setFilter(value)} />}
          </Box>
          {onClose && <Button
            variant="contained"
            color={"error"}
            onClick={onClose}
          >
            Close
          </Button>}
        </Box>
      </Box>
    </Box>
  );
}

export default ConfigurationApiTable;